/**
 * CardAiDocuments - Compact document list/upload card for V2 entities
 *
 * Fetches and displays documents associated with a specific source entity
 * (activity, workPlan, contract, condition, voucher) using the AI documents API.
 * Supports upload, delete, and opens the document viewer dialog.
 */

// react
import {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	DragEvent,
	ChangeEvent,
} from 'react';

// routing
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	alpha,
	Box,
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import {
	Add as IconAdd,
	CloudUpload as IconUpload,
	Delete as IconDelete,
	Description as IconDocument,
	Edit as IconEdit,
	ExpandLess as IconExpandLess,
	ExpandMore as IconExpandMore,
	Image as IconImage,
	InsertDriveFile as IconFile,
	OpenInNew as IconView,
	PictureAsPdf as IconPdf,
	TableChart as IconXlsx,
	TextSnippet as IconText,
} from '@mui/icons-material';

// components
import { CardDetail } from '../CardDetail/CardDetail';
import { DialogDocumentViewer } from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';
import type { ViewerDocumentInfo } from '../../dialogs/DialogDocumentViewer/DialogDocumentViewer';

// context
import { ContextUser } from '../../../App.context';

// api
import { fetchAiDocuments, updateAiDocument, uploadAiDocument } from '../../../services/aiApi';
import type { AiDocument } from '../../pages/ProjectV2/Documents/documentTypes';
import type { ContractDocument } from '../../../data/types';

// design system
import { formatDate } from '../../../utils/dates';
import { GRADIENTS } from '../../../data/colors';

// Types

/** Source types supported by the AI documents API */
export type AiDocumentSourceType =
	| 'activity'
	| 'workPlan'
	| 'contract'
	| 'condition'
	| 'voucher'
	| 'invoice'
	| 'component'
	| 'spendPlan';

/** Context describing where documents belong */
export interface AiDocumentContext {
	type: AiDocumentSourceType;
	id: number;
	name?: string;
}

/** A tag-based document category for grouped display */
export interface DocumentCategory {
	/** Tag value that matches metadata.tags[] entries */
	tag: string;
	/** Display label for the section header */
	label: string;
	/** Optional accent color (hex). Falls back to variant color. */
	color?: string;
}

interface CardAiDocumentsProps {
	/** Context of the parent entity (activity, workPlan, contract, condition, voucher) */
	context: AiDocumentContext;
	/** Project ID for document isolation — required by the upload API */
	projectId?: number;
	/** Callback when a document is clicked for viewing */
	onViewDocument?: (doc: AiDocument) => void;
	/** Callback when a document is deleted - if provided, shows delete button */
	onDelete?: (doc: AiDocument) => Promise<void>;
	/** Maximum file size in MB */
	maxFileSizeMB?: number;
	/** Accepted file types */
	acceptedTypes?: string;
	/** Whether to allow multiple file uploads */
	multiple?: boolean;
	/** Custom title */
	title?: string;
	/** Whether the card is read-only */
	readOnly?: boolean;
	/** Refresh trigger - increment to refresh documents */
	refreshKey?: number;
	/**
	 * Optional tag-based categories for grouped display.
	 * When provided, documents are grouped into collapsible sections by their metadata.tags.
	 * Each section gets its own compact drop zone for targeted uploads.
	 * Documents with no matching tag appear in an "Other" section.
	 * When omitted, the flat list behaviour is preserved.
	 */
	categories?: DocumentCategory[];
}

// Helpers

const formatFileSize = (bytes?: number): string => {
	if (!bytes) return '';
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Resolve the file icon. Falls back to filename extension when doc_type is generic. */
const getFileIcon = (docType: string, filename?: string) => {
	const resolve = (ext: string) => {
		const lower = ext.toLowerCase();
		if (lower === 'pdf') return IconPdf;
		if (['xlsx', 'xls', 'csv'].includes(lower)) return IconXlsx;
		if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(lower)) return IconImage;
		if (['txt', 'md', 'json'].includes(lower)) return IconText;
		if (['doc', 'docx'].includes(lower)) return IconDocument;
		return null;
	};

	const fromType = resolve(docType);
	if (fromType) return fromType;

	if (filename) {
		const ext = filename.split('.').pop() || '';
		const fromExt = resolve(ext);
		if (fromExt) return fromExt;
	}

	return IconFile;
};

/** Resolve a human-readable type label. Falls back to filename extension when
 *  doc_type is generic (e.g. "document"). */
const getFileTypeLabel = (docType: string, filename?: string): string => {
	const resolve = (ext: string): string | null => {
		const lower = ext.toLowerCase();
		if (lower === 'pdf') return 'PDF';
		if (['xlsx', 'xls'].includes(lower)) return 'Excel';
		if (lower === 'csv') return 'CSV';
		if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(lower)) return 'Image';
		if (['doc', 'docx'].includes(lower)) return 'Word';
		if (['ppt', 'pptx'].includes(lower)) return 'PPT';
		return null;
	};

	const fromType = resolve(docType);
	if (fromType) return fromType;

	// doc_type is generic — try to infer from filename extension
	if (filename) {
		const ext = filename.split('.').pop() || '';
		const fromExt = resolve(ext);
		if (fromExt) return fromExt;
	}

	return docType.toUpperCase();
};

/** Map context type to API source_type format */
const getApiSourceType = (type: AiDocumentSourceType): string => {
	// API expects these exact strings
	return type;
};

/** Map context type to CardDetail variant */
const getVariant = (
	type: AiDocumentSourceType
): 'activity' | 'workPlan' | 'contract' | 'condition' => {
	if (type === 'voucher') return 'condition'; // voucher uses condition styling
	return type as 'activity' | 'workPlan' | 'contract' | 'condition';
};

/** Get uploader display name from AiDocument */
const getUploaderName = (doc: AiDocument): string => {
	if (typeof doc.uploaded_by === 'object' && doc.uploaded_by !== null) {
		return doc.uploaded_by.displayName;
	}
	return `User ${doc.uploaded_by}`;
};

/** Transform AiDocument to ContractDocument for DialogDocumentViewer compatibility */
const toContractDocument = (doc: AiDocument): ContractDocument => {
	const metadata = doc.metadata || {};
	const sizeBytes = Number(metadata.file_size_bytes || 0);
	const uploaderName =
		typeof doc.uploaded_by === 'object' && doc.uploaded_by !== null
			? doc.uploaded_by.displayName
			: `User ${doc.uploaded_by}`;

	// Map doc_type to valid ContractDocumentType
	const docTypeLower = doc.doc_type.toLowerCase();
	const validTypes = ['pdf', 'doc', 'xlsx', 'image'] as const;
	const type = validTypes.includes(docTypeLower as (typeof validTypes)[number])
		? (docTypeLower as 'pdf' | 'doc' | 'xlsx' | 'image')
		: 'doc'; // Default to 'doc' for unknown types

	return {
		id: String(doc.id),
		name: doc.filename,
		category: 'other',
		type,
		size: formatFileSize(sizeBytes),
		uploadedAt: doc.uploaded_at,
		uploadedBy: uploaderName,
	};
};

// Component

export const CardAiDocuments = ({
	context,
	projectId,
	onViewDocument,
	onDelete,
	maxFileSizeMB = 400,
	acceptedTypes = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png',
	multiple = true,
	title,
	readOnly = false,
	refreshKey = 0,
	categories,
}: CardAiDocumentsProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { user } = useContext(ContextUser);
	const [searchParams, setSearchParams] = useSearchParams();

	// State
	const [documents, setDocuments] = useState<AiDocument[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isDragOver, setIsDragOver] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);

	// Categories state
	/** Tag that will be attached to the next upload (set when using a per-category drop zone) */
	const [selectedTag, setSelectedTag] = useState<string | null>(null);
	/** Which category sections are expanded — all expanded by default */
	const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
		if (!categories) return new Set<string>();
		return new Set(categories.map((c) => c.tag).concat('__other__'));
	});

	// Document viewer state (driven by URL search params)
	const docId = searchParams.get('doc_id') || undefined;
	const docTab = (searchParams.get('doc_tab') || 'document') as 'document' | 'insights';
	const docEdit = searchParams.get('doc_edit') === 'true';
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerIndex, setViewerIndex] = useState(0);

	const variant = getVariant(context.type);
	const variantColors = GRADIENTS[variant];

	// Fetch documents
	const fetchDocs = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await fetchAiDocuments({
				source_type: getApiSourceType(context.type),
				source_id: context.id,
				populate: 'uploaded_by,source',
				page_size: 50,
			});
			setDocuments(response.data);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load documents');
		} finally {
			setLoading(false);
		}
	}, [context.type, context.id]);

	// Initial fetch and refresh
	useEffect(() => {
		fetchDocs();
	}, [fetchDocs, refreshKey]);

	// Upload handler
	const handleUpload = useCallback(
		async (files: File[], tag?: string) => {
			if (!user?.id || files.length === 0) return;

			setIsUploading(true);
			setUploadProgress(0);

			try {
				const uploadTag = tag || selectedTag || undefined;
				const contextTag = `${getApiSourceType(context.type)}:${context.id}`;
				for (let i = 0; i < files.length; i++) {
					const file = files[i];
					await uploadAiDocument({
						file,
						source_type: getApiSourceType(context.type),
						source_id: context.id,
						tag: uploadTag ? `${contextTag},${uploadTag}` : contextTag,
						project_id: projectId,
					});
					setUploadProgress(Math.round(((i + 1) / files.length) * 100));
				}
				// Refresh document list
				await fetchDocs();
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Upload failed');
			} finally {
				setIsUploading(false);
				setUploadProgress(0);
				setSelectedTag(null);
			}
		},
		[user, context, fetchDocs, projectId, selectedTag]
	);

	// Delete handler
	const handleDelete = useCallback(
		async (doc: AiDocument) => {
			if (!onDelete) return;
			setDeletingId(doc.id);
			try {
				await onDelete(doc);
				await fetchDocs();
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Delete failed');
			} finally {
				setDeletingId(null);
			}
		},
		[onDelete, fetchDocs]
	);

	// Drag and drop handlers
	const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		async (e: DragEvent<HTMLDivElement>, tag?: string) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragOver(false);

			if (readOnly) return;

			const files = Array.from(e.dataTransfer.files).filter(
				(file) => file.size <= maxFileSizeMB * 1024 * 1024
			);
			if (files.length > 0) {
				await handleUpload(files, tag);
			}
		},
		[readOnly, maxFileSizeMB, handleUpload]
	);

	const handleFileSelect = useCallback(
		async (e: ChangeEvent<HTMLInputElement>) => {
			if (readOnly) return;

			const files = Array.from(e.target.files || []).filter(
				(file) => file.size <= maxFileSizeMB * 1024 * 1024
			);
			if (files.length > 0) {
				await handleUpload(files);
			}

			// Reset input
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		},
		[readOnly, maxFileSizeMB, handleUpload]
	);

	/** Open file picker targeted at a specific tag category */
	const handleUploadClick = (tag?: string) => {
		if (tag) setSelectedTag(tag);
		fileInputRef.current?.click();
	};

	/** Toggle a category section expanded / collapsed */
	const toggleCategory = useCallback((key: string) => {
		setExpandedCategories((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	}, []);

	// URL param helper — merges updates into current search params
	const updateParams = useCallback(
		(updates: Record<string, string | undefined>) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					Object.entries(updates).forEach(([key, value]) => {
						if (value === undefined || value === '') {
							next.delete(key);
						} else {
							next.set(key, value);
						}
					});
					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	// Sync viewer open state with URL params
	useEffect(() => {
		if (docId && documents.length > 0) {
			const index = documents.findIndex((d) => d.id === docId);
			if (index >= 0) {
				setViewerIndex(index);
				setViewerOpen(true);
			} else {
				setViewerOpen(false);
			}
		} else {
			setViewerOpen(false);
		}
	}, [docId, documents]);

	// Open document viewer via URL params
	const handleOpenViewer = useCallback(
		(doc: AiDocument) => {
			updateParams({ doc_id: doc.id, doc_tab: 'document' });
			// Also call external handler if provided
			onViewDocument?.(doc);
		},
		[updateParams, onViewDocument]
	);

	const handleCloseViewer = useCallback(() => {
		updateParams({ doc_id: undefined, doc_tab: undefined, doc_edit: undefined });
	}, [updateParams]);

	const handleNavigateViewer = useCallback(
		(index: number) => {
			setViewerIndex(index);
			const doc = documents[index];
			if (doc) {
				updateParams({ doc_id: doc.id });
			}
		},
		[documents, updateParams]
	);

	const handleTabChange = useCallback(
		(tab: 'document' | 'insights') => {
			updateParams({ doc_tab: tab });
		},
		[updateParams]
	);

	const handleEditChange = useCallback(
		(editing: boolean) => {
			updateParams({ doc_edit: editing ? 'true' : undefined });
		},
		[updateParams]
	);

	const handleViewerEditSave = useCallback(
		async (payload: { id: string; tags: string[] }) => {
			await updateAiDocument({
				id: payload.id,
				tags: payload.tags,
			});
			await fetchDocs();
		},
		[fetchDocs]
	);

	// Convert documents to ContractDocument format for viewer
	const viewerDocuments = useMemo(() => documents.map(toContractDocument), [documents]);

	// Blob filenames for viewer — use the raw filename (blob stored at bronze/{filename})
	const blobFilenames = useMemo(() => documents.map((doc) => doc.filename), [documents]);

	// Rich document info for the viewer's detail panel and AI insights
	const documentInfos = useMemo<ViewerDocumentInfo[]>(
		() =>
			documents.map((doc) => {
				const metadata = doc.metadata || {};
				const sizeBytes = Number(metadata.file_size_bytes || 0);
				const tags: string[] = Array.isArray(metadata.tags) ? (metadata.tags as string[]) : [];
				return {
					id: doc.id,
					name: doc.filename,
					tags,
					sourceType: context.type,
					sourceLabel: context.name,
					size: sizeBytes > 0 ? formatFileSize(sizeBytes) : undefined,
					uploadedAt: doc.uploaded_at,
					uploadedBy: getUploaderName(doc),
					jobId: doc.job_id || undefined,
				};
			}),
		[documents, context.type, context.name]
	);

	// Group documents by category tags when categories are provided
	const groupedDocuments = useMemo(() => {
		if (!categories || categories.length === 0) return null;

		const groups: Record<string, AiDocument[]> = {};
		// Initialise a bucket per category + "other"
		categories.forEach((c) => {
			groups[c.tag] = [];
		});
		groups['__other__'] = [];

		documents.forEach((doc) => {
			const metadata = doc.metadata || {};
			const tags: string[] = Array.isArray(metadata.tags) ? (metadata.tags as string[]) : [];
			let matched = false;
			categories.forEach((c) => {
				if (tags.includes(c.tag)) {
					groups[c.tag].push(doc);
					matched = true;
				}
			});
			if (!matched) {
				groups['__other__'].push(doc);
			}
		});

		return groups;
	}, [categories, documents]);

	// Header actions — when categories are used, each section has its own upload button
	const headerActions = !readOnly && !categories && (
		<Tooltip title={t('spendPlan.documents.upload', 'Upload Document')}>
			<IconButton
				size="small"
				onClick={() => handleUploadClick()}
				disabled={isUploading}
				sx={{
					color: variantColors.icon,
					'&:hover': { bgcolor: alpha(variantColors.icon, 0.1) },
				}}>
				<IconAdd />
			</IconButton>
		</Tooltip>
	);

	// =========================================================================
	// Render helpers
	// =========================================================================

	/** Render a single document row */
	const renderDocumentRow = (doc: AiDocument, accentColor: string) => {
		const FileIcon = getFileIcon(doc.doc_type, doc.filename);
		const metadata = doc.metadata || {};
		const sizeBytes = Number(metadata.file_size_bytes || 0);
		const uploaderName = getUploaderName(doc);
		const fileTypeLabel = getFileTypeLabel(doc.doc_type, doc.filename);
		const tags: string[] = Array.isArray(metadata.tags) ? (metadata.tags as string[]) : [];

		// Build a compact detail string: "Excel · Feb 18, 2026 · 14.8 KB"
		const details = [
			fileTypeLabel,
			formatDate({ date: doc.uploaded_at }),
			sizeBytes > 0 ? formatFileSize(sizeBytes) : '',
		]
			.filter(Boolean)
			.join(' · ');

		return (
			<Box key={doc.id} sx={{ position: 'relative' }}>
				<Stack
					direction="row"
					alignItems="center"
					spacing={1.5}
					sx={{
						py: 1.25,
						px: 1.5,
						borderRadius: 2,
						bgcolor: 'background.paper',
						border: '1px solid',
						borderColor: 'divider',
						transition: 'all 0.15s ease',
						cursor: 'pointer',
						'&:hover': {
							bgcolor: alpha(accentColor, 0.04),
							borderColor: alpha(accentColor, 0.25),
							boxShadow: `0 1px 4px ${alpha(accentColor, 0.1)}`,
							'& .doc-actions': { opacity: 1 },
						},
					}}
					onClick={() => handleOpenViewer(doc)}>
					{/* File icon with background */}
					<Box
						sx={{
							width: 42,
							height: 42,
							borderRadius: 1.5,
							bgcolor: alpha(accentColor, 0.1),
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}>
						<FileIcon sx={{ color: accentColor, fontSize: 22 }} />
					</Box>

					{/* File info — full width, actions overlay on hover */}
					<Box sx={{ flex: 1, minWidth: 0 }}>
						{/* Filename — allow up to 2 lines before truncating */}
						<Tooltip title={doc.filename} enterDelay={600} placement="top-start">
							<Typography
								variant="body2"
								sx={{
									fontWeight: 600,
									fontSize: '1.4rem',
									color: 'text.primary',
									lineHeight: 1.35,
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									wordBreak: 'break-word',
								}}>
								{doc.filename}
							</Typography>
						</Tooltip>
						{/* Details line: type · date · size */}
						<Typography
							variant="body2"
							sx={{
								color: 'text.secondary',
								fontSize: '1.3rem',
								lineHeight: 1.4,
								mt: 0.5,
							}}>
							{details}
						</Typography>
						{/* Uploader */}
						{uploaderName && (
							<Typography
								variant="body2"
								sx={{
									color: 'text.disabled',
									fontSize: '1.2rem',
									lineHeight: 1.4,
									mt: 0.25,
								}}>
								{uploaderName}
							</Typography>
						)}
						{/* Tags */}
						{tags.length > 0 && (
							<Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
								{tags.map((tag) => (
									<Chip
										key={tag}
										label={tag}
										size="small"
										variant="outlined"
										sx={{
											height: 20,
											fontSize: '1.1rem',
											borderColor: alpha(accentColor, 0.3),
											color: accentColor,
											'& .MuiChip-label': { px: 0.75 },
										}}
									/>
								))}
							</Stack>
						)}
					</Box>
				</Stack>

				{/* Floating actions — positioned over the row on hover so they don't eat filename space */}
				<Stack
					direction="row"
					spacing={0.5}
					className="doc-actions"
					sx={{
						position: 'absolute',
						top: 8,
						right: 8,
						opacity: 0,
						transition: 'opacity 0.15s ease',
						bgcolor: 'background.paper',
						borderRadius: 1.5,
						px: 0.5,
						py: 0.25,
						boxShadow: 1,
					}}
					onClick={(e) => e.stopPropagation()}>
					<Tooltip title="Open">
						<IconButton
							size="small"
							onClick={() => handleOpenViewer(doc)}
							sx={{
								color: accentColor,
								'&:hover': { bgcolor: alpha(accentColor, 0.12) },
							}}>
							<IconView sx={{ fontSize: 18 }} />
						</IconButton>
					</Tooltip>
					{!readOnly && (
						<Tooltip title="Edit">
							<IconButton
								size="small"
								onClick={() => updateParams({ doc_id: doc.id, doc_edit: 'true' })}
								sx={{
									color: theme.palette.info.main,
									'&:hover': { bgcolor: alpha(theme.palette.info.main, 0.12) },
								}}>
								<IconEdit sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					)}
					{!readOnly && onDelete && (
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={() => handleDelete(doc)}
								disabled={deletingId === doc.id}
								sx={{
									color: theme.palette.error.main,
									'&:hover': { bgcolor: alpha(theme.palette.error.main, 0.12) },
								}}>
								{deletingId === doc.id ? (
									<CircularProgress size={14} />
								) : (
									<IconDelete sx={{ fontSize: 18 }} />
								)}
							</IconButton>
						</Tooltip>
					)}
				</Stack>
			</Box>
		);
	};

	/** Render a compact drop zone (used inside category sections) */
	const renderCompactDropZone = (accentColor: string, tag?: string) => (
		<Paper
			elevation={0}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={(e) => handleDrop(e, tag)}
			onClick={() => handleUploadClick(tag)}
			sx={{
				background: isDragOver
					? `linear-gradient(135deg, ${alpha(accentColor, 0.08)} 0%, ${alpha(accentColor, 0.04)} 100%)`
					: alpha(theme.palette.grey[100], 0.5),
				border: '1.5px dashed',
				borderColor: isDragOver ? accentColor : alpha(theme.palette.grey[400], 0.3),
				borderRadius: 1.5,
				cursor: isUploading ? 'default' : 'pointer',
				py: 1,
				px: 1.5,
				textAlign: 'center',
				transition: 'all 0.2s ease',
				'&:hover': !isUploading
					? {
							background: `linear-gradient(135deg, ${alpha(accentColor, 0.08)} 0%, ${alpha(accentColor, 0.04)} 100%)`,
							borderColor: accentColor,
						}
					: undefined,
			}}>
			<Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75}>
				<IconUpload sx={{ color: alpha(accentColor, 0.6), fontSize: 20 }} />
				<Typography
					variant="body2"
					sx={{
						color: isDragOver ? accentColor : 'text.secondary',
						fontWeight: 500,
						fontSize: '1.25rem',
					}}>
					Drop or click to upload
				</Typography>
			</Stack>
		</Paper>
	);

	/** Render a full-size drop zone (used for flat list / no-documents state) */
	const renderFullDropZone = (accentColor: string, hasDocs: boolean) => (
		<Paper
			elevation={0}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={(e) => handleDrop(e)}
			onClick={() => handleUploadClick()}
			sx={{
				background: isDragOver
					? `linear-gradient(135deg, ${alpha(accentColor, 0.08)} 0%, ${alpha(accentColor, 0.04)} 100%)`
					: hasDocs
						? alpha(theme.palette.grey[100], 0.5)
						: `linear-gradient(135deg, ${alpha(accentColor, 0.04)} 0%, ${alpha(theme.palette.grey[100], 0.8)} 100%)`,
				border: '2px dashed',
				borderColor: isDragOver
					? accentColor
					: hasDocs
						? alpha(theme.palette.grey[400], 0.3)
						: alpha(accentColor, 0.2),
				borderRadius: 2,
				cursor: isUploading ? 'default' : 'pointer',
				py: hasDocs ? 1.5 : 3,
				px: 2,
				textAlign: 'center',
				transition: 'all 0.2s ease',
				'&:hover': !isUploading
					? {
							background: `linear-gradient(135deg, ${alpha(accentColor, 0.08)} 0%, ${alpha(accentColor, 0.04)} 100%)`,
							borderColor: accentColor,
							transform: 'translateY(-1px)',
						}
					: undefined,
			}}>
			<Stack
				direction={hasDocs ? 'row' : 'column'}
				alignItems="center"
				justifyContent="center"
				spacing={hasDocs ? 1 : 0.5}>
				<Box
					sx={{
						width: hasDocs ? 32 : 48,
						height: hasDocs ? 32 : 48,
						borderRadius: '50%',
						bgcolor: isDragOver ? alpha(accentColor, 0.15) : alpha(accentColor, 0.08),
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						transition: 'all 0.2s ease',
					}}>
					<IconUpload
						sx={{
							color: isDragOver ? accentColor : alpha(accentColor, 0.7),
							fontSize: hasDocs ? 18 : 24,
						}}
					/>
				</Box>
				<Box>
					<Typography
						variant="body2"
						sx={{
							color: isDragOver ? accentColor : 'text.secondary',
							fontWeight: 500,
							fontSize: '1.25rem',
						}}>
						{hasDocs
							? t('spendPlan.documents.dropMore', 'Add more files')
							: t('spendPlan.documents.dropHere', 'Drop files here or click to upload')}
					</Typography>
					{!hasDocs && (
						<Typography
							variant="body2"
							sx={{ color: 'text.disabled', display: 'block', fontSize: '1.25rem' }}>
							PDF, Word, Excel, Images up to {maxFileSizeMB}MB
						</Typography>
					)}
				</Box>
			</Stack>
		</Paper>
	);

	/** Render a collapsible category section */
	const renderCategorySection = (
		key: string,
		label: string,
		docs: AiDocument[],
		color: string,
		isLast: boolean
	) => {
		const isExpanded = expandedCategories.has(key);
		return (
			<Box key={key} sx={{ mb: isLast ? 0 : 1 }}>
				{/* Section header */}
				<Stack
					direction="row"
					alignItems="center"
					spacing={1}
					onClick={() => toggleCategory(key)}
					sx={{
						cursor: 'pointer',
						py: 0.75,
						px: 1,
						borderRadius: 1.5,
						transition: 'background 0.15s ease',
						'&:hover': { bgcolor: alpha(color, 0.06) },
					}}>
					<Box
						sx={{
							width: 6,
							height: 6,
							borderRadius: '50%',
							bgcolor: color,
							flexShrink: 0,
						}}
					/>
					<Typography
						variant="subtitle2"
						sx={{
							fontWeight: 600,
							fontSize: '1.25rem',
							color: 'text.primary',
							flex: 1,
						}}>
						{label}
					</Typography>
					<Chip
						label={docs.length}
						size="small"
						sx={{
							height: 24,
							minWidth: 28,
							fontSize: '1.25rem',
							fontWeight: 700,
							bgcolor: docs.length > 0 ? alpha(color, 0.12) : alpha(theme.palette.grey[400], 0.12),
							color: docs.length > 0 ? color : theme.palette.grey[500],
							'& .MuiChip-label': { px: 0.5 },
						}}
					/>
					{isExpanded ? (
						<IconExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} />
					) : (
						<IconExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
					)}
				</Stack>

				{/* Collapsible content */}
				<Collapse in={isExpanded}>
					<Box sx={{ pl: 1, pr: 0.5, pt: 0.5, pb: 0.5 }}>
						{docs.length > 0 && (
							<Stack spacing={0.75} sx={{ mb: !readOnly ? 1 : 0 }}>
								{docs.map((doc) => renderDocumentRow(doc, color))}
							</Stack>
						)}
						{!readOnly && renderCompactDropZone(color, key === '__other__' ? undefined : key)}
					</Box>
				</Collapse>
			</Box>
		);
	};

	// =========================================================================
	// Main render
	// =========================================================================

	return (
		<CardDetail
			icon={IconDocument}
			title={title || 'Documents'}
			subtitle={loading ? '' : `${documents.length} document${documents.length !== 1 ? 's' : ''}`}
			variant={variant}
			themeColor={variantColors.icon}
			headerActions={headerActions}>
			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept={acceptedTypes}
				multiple={multiple}
				onChange={handleFileSelect}
				style={{ display: 'none' }}
			/>

			<Box sx={{ p: 2 }}>
				{/* Upload progress */}
				{isUploading && (
					<Paper
						elevation={0}
						sx={{
							mb: 2,
							p: 1.5,
							bgcolor: alpha(variantColors.icon, 0.04),
							border: '1px solid',
							borderColor: alpha(variantColors.icon, 0.15),
							borderRadius: 2,
						}}>
						<Stack direction="row" alignItems="center" spacing={1.5}>
							<Box
								sx={{
									width: 36,
									height: 36,
									borderRadius: '50%',
									bgcolor: alpha(variantColors.icon, 0.1),
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
								}}>
								<CircularProgress size={20} thickness={5} sx={{ color: variantColors.icon }} />
							</Box>
							<Box sx={{ flex: 1 }}>
								<Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5, fontSize: '1.25rem' }}>
									{t('spendPlan.documents.uploading', 'Uploading...')}
								</Typography>
								<LinearProgress
									variant="determinate"
									value={uploadProgress}
									sx={{
										height: 6,
										borderRadius: 3,
										bgcolor: alpha(variantColors.icon, 0.1),
										'& .MuiLinearProgress-bar': {
											bgcolor: variantColors.icon,
											borderRadius: 3,
										},
									}}
								/>
							</Box>
							<Typography
								variant="body2"
								sx={{ fontWeight: 600, color: variantColors.icon, fontSize: '1.25rem' }}>
								{uploadProgress}%
							</Typography>
						</Stack>
					</Paper>
				)}

				{/* Error message */}
				{error && (
					<Paper
						elevation={0}
						sx={{
							mb: 2,
							p: 1.5,
							bgcolor: alpha(theme.palette.error.main, 0.04),
							border: '1px solid',
							borderColor: alpha(theme.palette.error.main, 0.2),
							borderRadius: 2,
						}}>
						<Typography
							variant="body2"
							sx={{ color: theme.palette.error.main, fontSize: '1.25rem' }}>
							{error}
						</Typography>
					</Paper>
				)}

				{/* Loading state */}
				{loading && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							py: 4,
						}}>
						<CircularProgress size={28} thickness={4} sx={{ color: variantColors.icon, mb: 1 }} />
						<Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1.25rem' }}>
							Loading documents...
						</Typography>
					</Box>
				)}

				{/* ---- Grouped view (categories provided) ---- */}
				{!loading && groupedDocuments && categories && (
					<Stack spacing={0}>
						{categories.map((cat, idx) => {
							const docs = groupedDocuments[cat.tag] || [];
							const color = cat.color || variantColors.icon;
							return renderCategorySection(
								cat.tag,
								cat.label,
								docs,
								color,
								idx === categories.length - 1 && (groupedDocuments['__other__']?.length ?? 0) === 0
							);
						})}
						{/* "Other" section — only if there are uncategorised docs or nothing uploaded yet */}
						{((groupedDocuments['__other__']?.length ?? 0) > 0 || documents.length === 0) &&
							renderCategorySection(
								'__other__',
								'Other',
								groupedDocuments['__other__'] || [],
								theme.palette.grey[600],
								true
							)}
					</Stack>
				)}

				{/* ---- Flat list view (no categories) ---- */}
				{!loading && !groupedDocuments && documents.length > 0 && (
					<Stack spacing={1} sx={{ mb: !readOnly ? 2 : 0 }}>
						{documents.map((doc) => renderDocumentRow(doc, variantColors.icon))}
					</Stack>
				)}

				{/* Flat drop zone (no categories) */}
				{!loading &&
					!readOnly &&
					!groupedDocuments &&
					renderFullDropZone(variantColors.icon, documents.length > 0)}

				{/* Empty state for read-only */}
				{!loading && readOnly && documents.length === 0 && !groupedDocuments && (
					<Box
						sx={{
							textAlign: 'center',
							py: 4,
							px: 2,
							bgcolor: alpha(theme.palette.grey[100], 0.5),
							borderRadius: 2,
						}}>
						<Box
							sx={{
								width: 48,
								height: 48,
								borderRadius: '50%',
								bgcolor: alpha(theme.palette.grey[400], 0.1),
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								mx: 'auto',
								mb: 1.5,
							}}>
							<IconDocument sx={{ fontSize: 24, color: alpha(theme.palette.grey[500], 0.6) }} />
						</Box>
						<Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1.25rem' }}>
							{t('spendPlan.documents.noDocuments', 'No documents uploaded')}
						</Typography>
					</Box>
				)}
			</Box>

			{/* Document Viewer Dialog */}
			<DialogDocumentViewer
				isOpen={viewerOpen}
				currentIndex={viewerIndex}
				documents={viewerDocuments}
				onClose={handleCloseViewer}
				onNavigate={handleNavigateViewer}
				blobFilenames={blobFilenames}
				documentInfos={documentInfos}
				initialTab={docTab}
				onTabChange={handleTabChange}
				onEditSave={readOnly ? undefined : handleViewerEditSave}
				initialEditing={docEdit}
				onEditChange={handleEditChange}
			/>
		</CardDetail>
	);
};

export default CardAiDocuments;
